import { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { BoardTemplate } from '../../mainApi';
import { TemplateShareInput } from '../../services/templates.service';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_RECIPIENTS = 10;

export const parseTemplateShareEmails = (value: string) => [
  ...new Set(
    value
      .split(/\s+/)
      .map((email) => email.trim().toLowerCase())
      .filter(Boolean)
  ),
];

type ShareTemplateDialogProps = {
  template: BoardTemplate | null;
  isSharing: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (input: TemplateShareInput) => void;
};

export const ShareTemplateDialog = ({
  template,
  isSharing,
  error,
  onClose,
  onSubmit,
}: ShareTemplateDialogProps) => {
  const [recipients, setRecipients] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [message, setMessage] = useState('');
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    if (!template) {
      return;
    }

    setRecipients([]);
    setEmailInput('');
    setMessage('');
    setEmailError('');
  }, [template]);

  const updateRecipients = (values: string[]) => {
    const normalizedRecipients = parseTemplateShareEmails(values.join(' '));
    const invalidEmail = normalizedRecipients.find((email) => !EMAIL_REGEX.test(email));

    if (invalidEmail) {
      setEmailError(`Invalid email address: ${invalidEmail}`);
      return false;
    }

    if (normalizedRecipients.length > MAX_RECIPIENTS) {
      setEmailError(`You can share with up to ${MAX_RECIPIENTS} people at a time`);
      return false;
    }

    setRecipients(normalizedRecipients);
    setEmailError('');
    return true;
  };

  const commitEmailInput = () => {
    const pendingRecipients = parseTemplateShareEmails(emailInput);
    if (pendingRecipients.length === 0) {
      return true;
    }

    if (!updateRecipients([...recipients, ...pendingRecipients])) {
      return false;
    }

    setEmailInput('');
    return true;
  };

  const handleSubmit = () => {
    setEmailError('');
    const pendingRecipients = parseTemplateShareEmails(emailInput);
    const submittedRecipients = parseTemplateShareEmails(
      [...recipients, ...pendingRecipients].join(' ')
    );

    if (submittedRecipients.length === 0) {
      setEmailError('Enter at least one email address');
      return;
    }

    if (submittedRecipients.length > MAX_RECIPIENTS) {
      setEmailError(`You can share with up to ${MAX_RECIPIENTS} people at a time`);
      return;
    }

    const invalidEmail = submittedRecipients.find((email) => !EMAIL_REGEX.test(email));
    if (invalidEmail) {
      setEmailError(`Invalid email address: ${invalidEmail}`);
      return;
    }

    onSubmit({
      emailAddresses: submittedRecipients.join(' '),
      message: message.trim(),
    });
  };

  return (
    <Dialog
      open={Boolean(template)}
      onClose={() => {
        if (!isSharing) {
          onClose();
        }
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Share template</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography>
            Share <strong>{template?.name || 'Untitled template'}</strong> with up to 10 people.
          </Typography>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Autocomplete
            multiple
            freeSolo
            open={false}
            options={[]}
            value={recipients}
            inputValue={emailInput}
            onChange={(_, values) => {
              const nextValues = values.flatMap((value) => parseTemplateShareEmails(value));
              void updateRecipients(nextValues);
            }}
            onInputChange={(_, value, reason) => {
              if (reason === 'reset') {
                return;
              }

              setEmailInput(value);
              if (emailError) {
                setEmailError('');
              }

              if (/\s/.test(value)) {
                const nextRecipients = parseTemplateShareEmails(value);
                if (updateRecipients([...recipients, ...nextRecipients])) {
                  setEmailInput('');
                }
              }
            }}
            renderTags={(values, getTagProps) =>
              values.map((email, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={email}
                  label={email}
                  size="small"
                  disabled={isSharing}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Email addresses"
                error={Boolean(emailError)}
                helperText={emailError || 'Press space or Enter after each email address.'}
                autoFocus
              />
            )}
            onBlur={() => {
              void commitEmailInput();
            }}
            onKeyDown={(event) => {
              if ((event.key === 'Enter' || event.key === 'Tab') && emailInput.trim()) {
                event.preventDefault();
                void commitEmailInput();
              }
            }}
            disabled={isSharing}
            fullWidth
            sx={{
              '& .MuiAutocomplete-inputRoot': {
                alignItems: 'flex-start',
                minHeight: 88,
              },
            }}
          />
          <TextField
            label="Message (optional)"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            disabled={isSharing}
            fullWidth
            multiline
            minRows={4}
            inputProps={{ maxLength: 2000 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSharing}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isSharing}>
          {isSharing ? 'Sharing...' : 'Share'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
